import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CasinoGame } from 'types/CasinoGame';
import { casinoProviderLabel } from 'helpers/casinoProvider';
import styles from './ClassicCasinoRail.module.css';

interface ProviderGames {
  provider: string;
  games: CasinoGame[];
}

interface Props {
  providers: ProviderGames[];
  /** The player's starred games, newest first. */
  favorites: CasinoGame[];
  /** One playable tile (the list owns launching a game); lazy image. */
  tile: (game: CasinoGame, lazy?: boolean) => ReactNode;
  /** VIP live casino: the rail lists roulette, baccarat... not providers. */
  byGameType?: boolean;
}

const PAGE = 40;
const ALL = '';
const FAVORITES = '\u0000favorites';

/** The table's game, from its name: the feed has no such field. */
const GAME_TYPES: [string, RegExp][] = [
  ['vip_casino_roulette', /roulette|rulet/i],
  ['vip_casino_baccarat', /baccarat|bakara/i],
  ['vip_casino_blackjack', /blackjack/i],
];
const OTHER_TYPE = 'market_group_other';

const gameTypeOf = (game: CasinoGame) =>
  GAME_TYPES.find(([, name]) => name.test(game.name))?.[0] ?? OTHER_TYPE;

/**
 * Classic lobby on a phone, as the live list: the providers on a rail that
 * scrolls sideways (with their game counts, starred games first when there
 * are any), and the games of the picked one listed by name below, more on
 * demand.
 */
export function ClassicCasinoRail({
  providers,
  favorites,
  tile,
  byGameType = false,
}: Props) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState(ALL);
  const [shown, setShown] = useState(PAGE);

  const withProviders = useMemo(
    () => providers.filter((p) => p.provider && p.games?.length > 0),
    [providers]
  );
  const withGames = useMemo(() => {
    if (!byGameType) return withProviders;
    const games = withProviders.flatMap((p) => p.games);
    return [...GAME_TYPES.map(([key]) => key), OTHER_TYPE]
      .map((key) => ({
        provider: key,
        games: games.filter((g) => gameTypeOf(g) === key),
      }))
      .filter((x) => x.games.length > 0);
  }, [withProviders, byGameType]);
  const all = useMemo(() => withGames.flatMap((p) => p.games), [withGames]);
  const current = picked === FAVORITES && favorites.length === 0 ? ALL : picked;
  const games =
    current === ALL
      ? all
      : current === FAVORITES
        ? favorites
        : withGames.find((p) => p.provider === current)?.games ?? all;

  const item = (key: string, label: string, count: number) => (
    <button
      type="button"
      key={key}
      className={classNames(styles.item, key === current && styles.itemOn)}
      onClick={() => {
        setPicked(key);
        setShown(PAGE);
      }}
    >
      <span className={styles.name}>{label}</span>
      <span className={styles.count}>{count}</span>
    </button>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.rail}>
        {item(ALL, t('casino_all'), all.length)}
        {favorites.length > 0 &&
          item(FAVORITES, `⭐ ${t('casino_favorites')}`, favorites.length)}
        {withGames.map((p) =>
          item(
            p.provider,
            byGameType ? t(p.provider) : casinoProviderLabel(p.provider),
            p.games.length
          )
        )}
      </div>
      <div className={styles.grid}>
        {games.slice(0, shown).map((g) => tile(g, true))}
      </div>
      {games.length > shown && (
        <button
          type="button"
          className={styles.more}
          onClick={() => setShown((n) => n + PAGE)}
        >
          {t('casino_show_more', { count: games.length - shown })}
        </button>
      )}
    </div>
  );
}
